import { Link } from "react-router-dom";
import { LegalLayout, type Section } from "./LegalLayout.js";

/*
 * /analytics — transparency page about usage measurement.
 *
 * The brand README is explicit: no third-party trackers, no ad
 * networks, no Google Analytics, no Meta Pixel. This page exists so
 * a curious visitor can see exactly what we do measure (first-party,
 * anonymous) and what we never measure (the content of reflections,
 * individual identifiers).
 *
 * The "tools" list is honest about the current state: nothing is
 * wired up yet on the public deployment. When we add Plausible or a
 * Cloudflare Web Analytics token, this page gets updated.
 */
const SECTIONS: Section[] = [
  {
    id: "why",
    title: "Why this page exists",
    body: (
      <>
        <p>
          Most apps have an analytics page hidden inside their privacy
          policy. We thought it deserved its own room. If you want to know
          what numbers we look at — and what numbers we deliberately don&apos;t —
          you&apos;re in the right place.
        </p>
        <p>
          For the broader privacy picture, see the{" "}
          <Link to="/privacy">privacy page</Link>.
        </p>
      </>
    ),
  },
  {
    id: "measure",
    title: "What we measure",
    body: (
      <>
        <p>
          First-party, anonymous, aggregated. The owl reading these reports
          can&apos;t tell what any one person did — only what the room as a
          whole is doing.
        </p>
        <div className="def-table">
          <div className="def-row">
            <div className="k">Pageviews</div>
            <div className="v">Which screens are opened, in what order, and how
              long the page stays in focus. <strong>Path only</strong> — never
              the contents of a reflection.</div>
          </div>
          <div className="def-row">
            <div className="k">Sessions</div>
            <div className="v">A loose count of distinct visits per day, derived
              from anonymized device hash that resets on a short cycle.</div>
          </div>
          <div className="def-row">
            <div className="k">Feature use</div>
            <div className="v">Counts of: resets started / finished, moods
              picked (by mood, not by person), chats opened, library tabs
              viewed.</div>
          </div>
          <div className="def-row">
            <div className="k">Performance</div>
            <div className="v">Page load time, time to first interactive, and
              error rates. We use these to fix what feels slow.</div>
          </div>
          <div className="def-row">
            <div className="k">Region</div>
            <div className="v">Country and city-level region derived from your
              IP at the edge. The IP itself is hashed within 24 hours.</div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: "never",
    title: "What we never measure",
    body: (
      <>
        <div className="callout">
          <div className="label">Off-limits</div>
          <p>
            No reflection text. No mood notes. No chat content. No way to
            point at a row in our data and say &ldquo;that was you.&rdquo;
          </p>
        </div>
        <ul>
          <li><strong>The text of your reflections</strong> — never sent to any
            analytics tool, never logged for product use, never seen by
            anyone but you and the AI model that responds to you.</li>
          <li><strong>Your name or email</strong> — analytics events are tied
            to a rotating anonymous device hash, not to your account.</li>
          <li><strong>Cross-site activity</strong> — we have no way to know
            what other websites you visit.</li>
          <li><strong>Advertising IDs</strong> — IDFA, AAID, browser
            fingerprinting. None of these.</li>
          <li><strong>Third-party trackers</strong> — no Google Analytics,
            Meta Pixel, TikTok Pixel, LinkedIn Insight, Hotjar, FullStory,
            Mixpanel, Segment, or anything similar.</li>
        </ul>
      </>
    ),
  },
  {
    id: "tools",
    title: "Tools we use",
    body: (
      <>
        <p>
          The honest current state, listed by purpose. We update this list
          whenever we add or remove a tool.
        </p>
        <div className="def-table">
          <div className="def-row">
            <div className="k">Today (early access)</div>
            <div className="v">
              <strong>Nothing yet.</strong> The deployed bundle ships with no
              analytics pixel. Visits are visible only as anonymous
              Cloudflare edge-request counts, which we can see in our
              dashboard but you can&apos;t be identified in.
            </div>
          </div>
          <div className="def-row">
            <div className="k">When we add measurement</div>
            <div className="v">
              We&apos;ll use <a href="https://plausible.io" target="_blank" rel="noreferrer">Plausible</a>{" "}
              (privacy-respecting, GDPR-friendly, no cookies) or{" "}
              <a href="https://www.cloudflare.com/web-analytics/" target="_blank" rel="noreferrer">Cloudflare
              Web Analytics</a> — both first-party, both cookieless. We&apos;ll
              update this page on the day we wire it in.
            </div>
          </div>
          <div className="def-row">
            <div className="k">Server-side</div>
            <div className="v">
              Cloudflare Workers logs (counts, errors, response times). No
              request bodies are persisted past a short rolling window.
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies we set",
    body: (
      <>
        <p>The minimum the app can run on. All first-party.</p>
        <div className="def-table">
          <div className="def-row">
            <div className="k">wo.token</div>
            <div className="v">Your sign-in token. Strictly necessary. Stored
              in <code>localStorage</code>, not a cookie. Cleared on sign out.</div>
          </div>
          <div className="def-row">
            <div className="k">wo.demo.*</div>
            <div className="v">Demo-mode placeholders used while we&apos;re in
              early access. Also localStorage. Cleared on sign out.</div>
          </div>
          <div className="def-row">
            <div className="k">Cloudflare</div>
            <div className="v">A handful of small cookies the edge sets for
              bot-protection and load-balancing. Strictly necessary; not used
              to profile you.</div>
          </div>
        </div>
        <p>
          We do not set advertising cookies. We do not set third-party
          analytics cookies.
        </p>
      </>
    ),
  },
  {
    id: "optout",
    title: "Opting out",
    body: (
      <>
        <p>For non-essential measurement, you have three levers:</p>
        <ul>
          <li><strong>From your profile</strong> — open{" "}
            <Link to="/profile">Profile → Privacy</Link> and turn off
            &ldquo;Use my reflections to improve Wise Owl Talks.&rdquo; This is
            off by default.</li>
          <li><strong>Browser-level Do Not Track</strong> — we honour
            <code> Sec-GPC: 1</code> and <code>DNT: 1</code> headers. If
            your browser sends them, we treat your visit as opted-out of
            non-essential measurement.</li>
          <li><strong>Block in-app</strong> — content-blockers like uBlock
            Origin will block any third-party pixel we ever add. (We&apos;ll
            also let you turn it off from inside Wise Owl Talks, but defence in
            depth doesn&apos;t hurt.)</li>
        </ul>
        <p>
          Strictly necessary measurement (the sign-in session, error
          reporting at the edge) cannot be turned off — the app can&apos;t
          run without it.
        </p>
      </>
    ),
  },
  {
    id: "questions",
    title: "Questions",
    body: (
      <>
        <p>
          Write to <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>{" "}
          if anything on this page reads wrong, looks outdated, or just
          raises a question. A real person replies.
        </p>
        <p className="reflect">&ldquo;Measurement should describe a room, not catalogue its people.&rdquo;</p>
      </>
    ),
  },
];

export const Analytics = () => (
  <LegalLayout
    page="analytics"
    eyebrow="Transparency"
    title={<>How we measure usage.</>}
    lede="What's anonymous and aggregate, what we never look at, the tools we use, and how to opt out."
    updatedISO="2026-05-21"
    sections={SECTIONS}
  />
);
